import type * as THREE from "three";
import { pickInteractive } from "./Raycaster.js";
import type { PickableUserData } from "./Raycaster.js";

// ── Mode types ────────────────────────────────────────────────────────────────

/** The two active game states where clicks are processed. */
export type ActiveMode = "exploring" | "combat";

/** Full mode, including the frozen state where clicks are ignored. */
export type InteractionMode = ActiveMode | "frozen";

// ── Dispatch context ──────────────────────────────────────────────────────────

/**
 * Payload passed to every predicate and action.
 * Future tasks can extend this shape (sneak flag, spellTargeting flag, etc.)
 * by adding optional fields — existing predicates will safely ignore them.
 */
export interface DispatchContext {
  readonly mode: ActiveMode;
  readonly hit: PickableUserData;
}

export type DispatchPredicate = (ctx: DispatchContext) => boolean;
export type DispatchAction = (ctx: DispatchContext) => void;

// ── Internal ──────────────────────────────────────────────────────────────────

interface Rule {
  readonly predicate: DispatchPredicate;
  readonly action: DispatchAction;
}

type RaycastFn = (
  ndc: THREE.Vector2,
  camera: THREE.Camera,
  scene: THREE.Scene,
) => PickableUserData | null;

// ── InteractionDispatcher ─────────────────────────────────────────────────────

/**
 * Context-sensitive single click dispatcher (F07).
 *
 * Replaces the ad-hoc `canvas.addEventListener("click", ...)` block in
 * main.tsx. Rules are (predicate, action) pairs evaluated in registration
 * order; the first match fires and stops further evaluation.
 *
 * Extension points:
 *   - S06 (spell targeting): prepend a high-priority spell-commit rule
 *   - N08 (pickpocket): register sneak-context NPC rule before the talk rule
 *   - I-series (loot): register a loot-pile rule
 */
export class InteractionDispatcher {
  private readonly rules: Rule[] = [];
  private readonly raycast: RaycastFn;

  /**
   * @param getMode  Returns the current interaction mode; called on every
   *   click. When "frozen" the dispatcher short-circuits immediately.
   * @param raycast  Defaults to the engine's `pickInteractive`. Override in
   *   tests to avoid needing a real Three.js scene.
   */
  constructor(
    private readonly getMode: () => InteractionMode,
    raycast: RaycastFn = pickInteractive,
  ) {
    this.raycast = raycast;
  }

  /**
   * Register a dispatch rule.
   * Rules fire in registration order; first match wins.
   * Returns an unregister function for cleanup.
   */
  register(predicate: DispatchPredicate, action: DispatchAction): () => void {
    const rule: Rule = { predicate, action };
    this.rules.push(rule);
    return () => {
      const idx = this.rules.indexOf(rule);
      if (idx !== -1) this.rules.splice(idx, 1);
    };
  }

  /**
   * Register a dispatch rule at the front of the list — it is evaluated
   * before any rule registered with `register`. Used by S06 (spell
   * targeting) so that an armed spell intercepts clicks before the
   * default talk / attack / loot rules.
   */
  registerHighPriority(
    predicate: DispatchPredicate,
    action: DispatchAction,
  ): () => void {
    const rule: Rule = { predicate, action };
    this.rules.unshift(rule);
    return () => {
      const idx = this.rules.indexOf(rule);
      if (idx !== -1) this.rules.splice(idx, 1);
    };
  }

  /**
   * Entry point — call this from the canvas click handler.
   * Skips all processing when the game world is frozen.
   */
  handleClick(ndc: THREE.Vector2, camera: THREE.Camera, scene: THREE.Scene): void {
    const mode = this.getMode();
    if (mode === "frozen") return;

    const hit = this.raycast(ndc, camera, scene);
    if (!hit) return;

    const ctx: DispatchContext = { mode, hit };
    for (const rule of this.rules) {
      if (rule.predicate(ctx)) {
        rule.action(ctx);
        return;
      }
    }
  }
}
